import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/api';
import AuthContext from '../../contexts/AuthContext';
import '../../styles/NewMessagePage.css';

const NewMessagePage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [recipients, setRecipients] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    recipientType: 'individual',
    recipientId: '',
    halaqaId: '',
    // subject field removed but kept in state for compatibility
    subject: 'Message', // Default subject since it's removed from UI
    content: '',
    attachment: null,
    sendSms: false,
    tag: 'general',
    selectAllRecipients: false // Flag to track "Select All" option
  });
  
  // Debug state to track halaqaId for troubleshooting
  const [debug, setDebug] = useState({
    lastHalaqaId: '',
    lastRecipientType: 'individual'
  });

  // Add templates state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [messageType, setMessageType] = useState('general'); // For template filtering
  const [recipientFilter, setRecipientFilter] = useState('student'); // For filtering recipients (student, parent, class, absent, late)

  // Function to fetch recipients based on filter
  const fetchRecipientsByFilter = async (filter) => {
    try {
      setLoading(true);

      let endpoint = '/messages/recipients';

      // Different endpoints based on filter
      // The date parameter is required by the backend
      const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
      
      if (filter === 'absent') {
        endpoint = `/messages/attendance-recipients?status=absent&date=${today}`;
      } else if (filter === 'late') {
        endpoint = `/messages/attendance-recipients?status=late&date=${today}`;
      }

      console.log(`Fetching ${filter} recipients from: ${endpoint}`);

      const response = await api.get(endpoint);

      let recipientsData = [];

      if (response.data) {
        if (response.data.success !== undefined) {
          recipientsData = response.data.success ? response.data.data : [];
        } else if (Array.isArray(response.data)) {
          recipientsData = response.data;
        } else if (typeof response.data === 'object' && response.data.data) {
          recipientsData = Array.isArray(response.data.data) ? response.data.data : [];
        }
      }

      console.log(`Processed ${filter} recipients:`, recipientsData);

      // For attendance filters, we get a different structure
      if (filter === 'absent' || filter === 'late') {
        // Transform data to match the standard recipient format
        const transformedData = recipientsData.map(item => ({
          ...item.parent,
          recipientType: 'parent',
          childAttendanceRecords: item.childAttendanceRecords,
          attendanceStatus: filter
        }));

        setRecipients(transformedData);
      } else {
        setRecipients(recipientsData);
      }

      return true;
    } catch (err) {
      console.error(`Error fetching ${filter} recipients:`, err);
      
      // Enhanced error logging for attendance recipients
      if (filter === 'absent' || filter === 'late') {
        console.error('Attendance API error details:', {
          status: err.response?.status,
          message: err.response?.data?.message || err.message,
          filter: filter // Use filter instead of undefined endpoint variable
        });
        
        // Set a user-friendly error message
        if (err.response?.data?.message) {
          setError(`Failed to load ${filter} students: ${err.response.data.message}`);
        } else {
          setError(`Failed to load ${filter} students. There may be no ${filter} students recorded for today.`);
        }
      }
      
      setRecipients([]);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Define fetchData outside useEffect so it can be called for retry
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching data for NewMessagePage');

      // Fetch recipients based on current filter
      await fetchRecipientsByFilter(recipientFilter);

      // If user is admin or teacher, fetch classes/halaqas
      if (user && (user.role === 'admin' || user.role === 'teacher' || user.role === 'super_admin')) {
        try {
          const classesResponse = await api.get('/halaqas');
          console.log('Classes response:', classesResponse);

          if (classesResponse.data) {
            let classesData;

            if (classesResponse.data.success !== undefined) {
              classesData = classesResponse.data.success ? classesResponse.data.data : [];
            } else if (Array.isArray(classesResponse.data)) {
              classesData = classesResponse.data;
            } else if (typeof classesResponse.data === 'object' && classesResponse.data.data) {
              classesData = Array.isArray(classesResponse.data.data)
                ? classesResponse.data.data
                : [];
            } else {
              classesData = [];
            }

            console.log('Processed classes:', classesData);
            setClasses(classesData);
          }
        } catch (classesErr) {
          console.error('Error fetching classes:', classesErr);
          // Don't fail the whole page, just show empty classes
          setClasses([]);
        }
      }

      // Fetch message templates - general template by default
      try {
        const templateTag = 'general'; // Default template tag
        const templatesResponse = await api.get(`/messages/templates/${templateTag}`);
        console.log('Templates response:', templatesResponse);

        if (templatesResponse.data) {
          let templatesData = [];

          if (templatesResponse.data.success && templatesResponse.data.data) {
            templatesData = templatesResponse.data.data;
          } else if (Array.isArray(templatesResponse.data)) {
            templatesData = templatesResponse.data;
          }

          console.log('Processed templates:', templatesData);
          setTemplates(templatesData);
        }
      } catch (templatesErr) {
        console.error('Error fetching templates:', templatesErr);
        // Don't fail the whole page, just show empty templates
        setTemplates([]);

        // If no templates exist, try to create default templates
        try {
          console.log('Attempting to create default templates');
          await api.post('/messages/templates/defaults');

          // Try fetching again
          const retryResponse = await api.get('/messages/templates/general');
          if (retryResponse.data && retryResponse.data.data) {
            setTemplates(retryResponse.data.data);
          }
        } catch (createErr) {
          console.error('Error creating default templates:', createErr);
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch templates by tag
  const fetchTemplatesByTag = async (tag) => {
    try {
      setLoading(true);

      console.log(`Fetching templates for tag: ${tag}`);
      const templatesResponse = await api.get(`/messages/templates/${tag}`);

      if (templatesResponse.data) {
        let templatesData = [];

        if (templatesResponse.data.success && templatesResponse.data.data) {
          templatesData = templatesResponse.data.data;
        } else if (Array.isArray(templatesResponse.data)) {
          templatesData = templatesResponse.data;
        }

        console.log(`Processed ${tag} templates:`, templatesData);
        setTemplates(templatesData);

        // Reset selected template when changing tags
        setSelectedTemplate('');
      }
    } catch (err) {
      console.error(`Error fetching ${tag} templates:`, err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle message type change
  const handleMessageTypeChange = (type) => {
    setMessageType(type);
    fetchTemplatesByTag(type);

    // Update tag in form data
    setFormData(prev => ({
      ...prev,
      tag: type
    }));
  };

  // Handle recipient filter change
  const handleRecipientFilterChange = (filter) => {
    setRecipientFilter(filter);

    // Update recipientType and reset selections
    if (filter === 'class') {
      setFormData(prev => ({
        ...prev,
        recipientType: 'class',
        recipientId: ''
        // Don't reset halaqaId when switching to class tab
      }));
    } else {
      // For student, parent, absent, late tabs
      setFormData(prev => ({
        ...prev,
        recipientType: 'individual',
        recipientId: ''
        // Don't reset halaqaId when switching tabs - keep it for when switching back to class tab
      }));
    }

    // Fetch new recipients based on filter
    fetchRecipientsByFilter(filter);

    // When switching to absent or late, update the message type automatically
    if (filter === 'absent') {
      handleMessageTypeChange('attendance-absent');
    } else if (filter === 'late') {
      handleMessageTypeChange('attendance-late');
    } else if (filter !== 'class' && filter !== 'parent' && filter !== 'student') {
      // Reset to general for other tabs
      handleMessageTypeChange('general');
    }
  };

  // Use effect to call fetchData on component mount
  useEffect(() => {
    fetchData();
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        attachment: e.target.files[0] || null
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      // Handle special case for "Select All" option
      if (name === 'recipientId' && value === 'all') {
        // Store this special value to handle it in sendMessage
        setFormData(prev => ({
          ...prev,
          [name]: value,
          selectAllRecipients: true // Flag to indicate all recipients are selected
        }));
        console.log('Selected all recipients');
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          selectAllRecipients: false // Reset flag when selecting individual recipient
        }));
      }
      
      // Track halaqaId changes for debugging
      if (name === 'halaqaId' && value) {
        console.log('Selected halaqaId changed to:', value);
        setDebug(prev => ({
          ...prev,
          lastHalaqaId: value
        }));
      }
    }
  };

  // Handle template selection
  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);

    if (!templateId) {
      // If no template selected, don't change content
      return;
    }

    // Find the selected template
    const template = templates.find(t => t._id === templateId);
    if (template) {
      // Update form data with template content
      // Note: We're still setting subject internally even though UI field is removed
      setFormData(prev => ({
        ...prev,
        subject: template.subject || 'Message', // Subject field is removed from UI but kept internally
        content: template.content || prev.content,
        tag: template.tag || 'general'
      }));
    }
  };

  // Get example templates by message type
  const getExampleTemplate = (type) => {
    const templates = {
      'general': {
        subject: 'Important Information',
        content: 'Dear {{parentName}},\n\nI wanted to inform you about an important update regarding your child\'s class. Please let me know if you have any questions.\n\nThank you,\nSchool Administration'
      },
      'attendance-absent': {
        subject: 'Absence Notification',
        content: 'Dear {{parentName}},\n\nThis message is to inform you that your child was marked absent from class today. Please let us know if they will be returning tomorrow.\n\nThank you,\nSchool Administration'
      },
      'attendance-late': {
        subject: 'Late Arrival Notification',
        content: 'Dear {{parentName}},\n\nThis message is to inform you that your child arrived late to class today. Please ensure they arrive on time for future classes.\n\nThank you,\nSchool Administration'
      },
      'payment-reminder': {
        subject: 'Payment Reminder',
        content: 'Dear {{parentName}},\n\nThis is a friendly reminder that your payment is due. Please make your payment at your earliest convenience.\n\nThank you,\nSchool Administration'
      }
    };

    return templates[type] || templates['general'];
  };

  // Use example template
  const useExampleTemplate = () => {
    const template = getExampleTemplate(messageType);

    setFormData(prev => ({
      ...prev,
      subject: template.subject,
      content: template.content,
      tag: messageType
    }));
  };

  // Process template placeholders
  const processPlaceholders = (text, recipientId) => {
    if (!text) return '';

    // Basic placeholder replacements
    let processed = text;

    // For group messages, replace placeholders with generic terms
    if (formData.recipientType === 'class') {
      processed = processed.replace(/{{recipientName}}/g, 'Parent');
      processed = processed.replace(/{{parentName}}/g, 'Parent');
      processed = processed.replace(/{{studentName}}/g, 'Student');
      console.log('Processed group message placeholders:', processed);
      return processed;
    }

    // For individual messages, try to use the specific recipient info
    const recipient = recipients.find(r => r._id === recipientId);
    if (recipient) {
      processed = processed.replace(/{{recipientName}}/g, recipient.name || 'Parent');
      processed = processed.replace(/{{parentName}}/g, recipient.name || 'Parent');
      
      // If recipient is a parent, try to get their child's name
      let studentName = 'Student';
      // Check if the recipient has child data in either format:
      
      // Format 1: childAttendanceRecords (from attendance API)
      if (recipient.childAttendanceRecords && recipient.childAttendanceRecords.length > 0) {
        const childRecord = recipient.childAttendanceRecords[0];
        if (childRecord && childRecord.studentId && childRecord.studentId.name) {
          studentName = childRecord.studentId.name;
          console.log('Found student name from childAttendanceRecords:', studentName);
        }
      } 
      // Format 2: parentInfo.children (from populated parent)
      else if (recipient.parentInfo && 
               recipient.parentInfo.children && 
               recipient.parentInfo.children.length > 0) {
        
        // If the children array contains populated user objects
        if (typeof recipient.parentInfo.children[0] === 'object') {
          studentName = recipient.parentInfo.children[0].name || 'Student';
          console.log('Found student name from parentInfo.children:', studentName);
        }
      }
      // Format 3: children (simplified format)
      else if (recipient.children && recipient.children.length > 0) {
        if (typeof recipient.children[0] === 'object') {
          studentName = recipient.children[0].name || 'Student';
          console.log('Found student name from children array:', studentName);
        }
      }
      
      processed = processed.replace(/{{studentName}}/g, studentName);
      console.log('Processed individual message placeholders for', recipient.name, ':', processed);
    } else {
      // Fallback if recipient not found
      processed = processed.replace(/{{recipientName}}/g, 'Parent');
      processed = processed.replace(/{{parentName}}/g, 'Parent');
      processed = processed.replace(/{{studentName}}/g, 'Student');
      console.log('Used fallback placeholders (no recipient found):', processed);
    }

    return processed;
  };

  const validateForm = () => {
    const errors = [];

    // Validate based on the active tab and recipient type
    if (recipientFilter === 'class' || formData.recipientType === 'class') {
      if (!formData.halaqaId) {
        errors.push('Please select a class');
      }
    } else {
      // For other tabs (student, parent, absent, late)
      if (!formData.recipientId) {
        errors.push(`Please select a ${recipientFilter === 'student' ? 'student' :
          recipientFilter === 'parent' ? 'parent' :
          `parent of ${recipientFilter} student`}`);
      }
      
      // If "Select All" is selected but there are no recipients, that's an error
      if (formData.recipientId === 'all' && recipients.length === 0) {
        errors.push(`No parents with ${recipientFilter} students available`);
      }
    }

    // Subject validation removed since the field was removed

    if (!formData.content.trim()) {
      errors.push('Message content is required');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();

    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    // Log form state before sending
    console.log('Preparing to send message with form state:', {
      recipientType: formData.recipientType,
      recipientId: formData.recipientId,
      halaqaId: formData.halaqaId || debug.lastHalaqaId,
      subject: formData.subject,
      content: formData.content.substring(0, 50) + '...',
      sendSms: formData.sendSms,
      tag: formData.tag,
      activeTab: recipientFilter,
      selectAllRecipients: formData.selectAllRecipients
    });
    
    // Check if "Select All" option is selected
    if (formData.selectAllRecipients && 
        (recipientFilter === 'absent' || recipientFilter === 'late') && 
        recipients.length > 0) {
      
      // Show confirmation if there are multiple recipients
      if (recipients.length > 1) {
        if (!window.confirm(`You are about to send this message to ${recipients.length} parents. Continue?`)) {
          console.log('User canceled sending to all recipients');
          return;
        }
      }
      
      console.log(`Preparing to send to all ${recipients.length} parents`);
    }
    
    // IMPORTANT FIX: If halaqaId is missing but we're in class mode, try to use the last known halaqaId
    if (formData.recipientType === 'class' && !formData.halaqaId && debug.lastHalaqaId) {
      console.log('Recovering missing halaqaId from debug state:', debug.lastHalaqaId);
      setFormData(prev => ({
        ...prev,
        halaqaId: debug.lastHalaqaId
      }));
    }

    setSending(true);

    try {
      // Prepare form data for file upload
      const messageData = new FormData();

      // Check if we're sending to all recipients
      if (formData.selectAllRecipients && 
          (recipientFilter === 'absent' || recipientFilter === 'late') && 
          recipients.length > 0) {
        
        // Create an array to collect all responses
        const responses = [];
        let errors = [];
        
        // Send messages to each recipient individually
        for (const recipient of recipients) {
          try {
            // Process content with recipient-specific placeholders
            const recipientProcessedContent = processPlaceholders(formData.content, recipient._id);
            
            console.log(`Sending message to ${recipient.name}:`, recipientProcessedContent.substring(0, 50) + '...');
            
            // Send individual message
            const response = await api.post('/messages', {
              recipient: recipient._id,
              subject: formData.subject,
              content: recipientProcessedContent,
              sendSms: formData.sendSms,
              tag: formData.tag
            });
            
            responses.push(response);
          } catch (err) {
            console.error(`Error sending message to ${recipient.name}:`, err);
            errors.push(`Failed to send to ${recipient.name}: ${err.message}`);
          }
        }
        
        // Check if any messages failed
        if (errors.length > 0) {
          if (errors.length === recipients.length) {
            // All messages failed
            throw new Error(`Failed to send all messages. First error: ${errors[0]}`);
          } else {
            // Some messages succeeded
            setError(`Sent ${responses.length} out of ${recipients.length} messages. Errors: ${errors.join('; ')}`);
            // Navigate back even with partial success
            navigate('/messages');
            return;
          }
        } else {
          // All messages succeeded
          console.log(`Successfully sent messages to all ${responses.length} recipients`);
          navigate('/messages');
          return;
        }
      }
      
      // If not sending to all, process message normally
      const processedContent = processPlaceholders(formData.content, formData.recipientId);

      console.log('Preparing to send message with form data:', {
        ...formData,
        content: processedContent
      });

      if (formData.recipientType === 'individual') {
        // Individual message - use /messages endpoint with recipient
        if (!formData.recipientId || !processedContent) {
          throw new Error('Please provide recipient and content');
        }

        // Create regular JSON payload instead of FormData for text-only messages
        let payload = {
          recipient: formData.recipientId,
          subject: formData.subject,
          content: processedContent,
          sendSms: formData.sendSms
        };

        // Add tag if it exists
        if (formData.tag) {
          payload.tag = formData.tag;
        }

        let response;

        // Use FormData only if there's an attachment, otherwise use JSON
        if (formData.attachment) {
          // Use FormData for attachments - match mobile app exactly
          messageData.append('recipient', formData.recipientId);
          messageData.append('content', processedContent);
          // Subject is optional for individual messages
          if (formData.subject) {
            messageData.append('subject', formData.subject);
          }
          
          // Only add sendSms if it's true
          if (formData.sendSms) {
            messageData.append('sendSms', 'true');
          }
          
          if (formData.tag) {
            messageData.append('tag', formData.tag);
          }
          
          messageData.append('attachment', formData.attachment);
          
          console.log('Sending individual message with attachment to recipient:', formData.recipientId);
          response = await api.post('/messages', messageData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } else {
          // Use JSON for text-only messages
          console.log('Sending individual message to recipient:', formData.recipientId);
          console.log('Payload:', payload);
          response = await api.post('/messages', payload);
        }

        console.log('Individual message response:', response);
      } else if (formData.recipientType === 'class') {
        // Get halaqaId - either from formData or from debug state as fallback
        const effectiveHalaqaId = formData.halaqaId || debug.lastHalaqaId;
        
        // Group/class message - use /messages/group endpoint
        if (!effectiveHalaqaId) {
          throw new Error('Please select a class before sending');
        }
        
        if (!processedContent) {
          throw new Error('Please provide message content');
        }

        console.log('Sending group message with halaqaId:', effectiveHalaqaId);
        
        // Create a simpler payload exactly matching the mobile app implementation
        // which has been proven to work
        let payload = {
          halaqaId: effectiveHalaqaId,
          content: processedContent,
          title: formData.subject || 'Group Message'
        };
        
        // Only add sendSms as an optional parameter
        if (formData.sendSms) {
          payload.sendSms = formData.sendSms;
        }

        // Add tag if it exists
        if (formData.tag) {
          payload.tag = formData.tag;
        }

        let response;

        // Use FormData only if there's an attachment, otherwise use JSON
        if (formData.attachment) {
          // Get effective halaqaId from form or debug state
          const effectiveHalaqaId = formData.halaqaId || debug.lastHalaqaId;
          
          // Use FormData for attachments - match mobile app exactly
          messageData.append('halaqaId', effectiveHalaqaId);
          messageData.append('content', processedContent);
          messageData.append('title', formData.subject || 'Group Message');
          
          // Only add sendSms if it's true
          if (formData.sendSms) {
            messageData.append('sendSms', 'true');
          }
          
          console.log('Appending halaqaId to FormData:', formData.halaqaId);
          
          if (formData.tag) {
            messageData.append('tag', formData.tag);
          }
          
          messageData.append('attachment', formData.attachment);
          
          console.log('Sending group message with attachment to class:', formData.halaqaId);
          response = await api.post('/messages/group', messageData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } else {
          // Use JSON for text-only messages
          console.log('Sending group message to class:', formData.halaqaId);
          console.log('Payload:', payload);
          response = await api.post('/messages/group', payload);
        }

        console.log('Group message response:', response);
      }

      // Navigate back to messages list on success
      navigate('/messages');
    } catch (err) {
      console.error('Error sending message:', err);
      if (err.response) {
        // Server returned an error response
        setError(`Failed to send message: ${err.response.data?.message || err.response.status}. Please try again.`);
      } else if (err.request) {
        // The request was made but no response was received
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        // Something else went wrong
        setError('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setSending(false);
    }
  };

  const canMessageClass = () => {
    return user && (user.role === 'admin' || user.role === 'teacher' || user.role === 'super_admin');
  };

  // Add CSS styles for new warning and info elements
  useEffect(() => {
    // Create a style element
    const styleEl = document.createElement('style');
    styleEl.setAttribute('id', 'message-page-styles');
    
    // Add the CSS rules
    styleEl.innerHTML = `
      .helper-text.warning {
        color: #d8000c;
        background-color: #ffbaba;
        padding: 10px;
        border-radius: 5px;
        margin-top: 8px;
        margin-bottom: 8px;
        display: flex;
        align-items: center;
      }
      .warning-icon {
        margin-right: 8px;
      }
      .info-text {
        color: #31708f;
        background-color: #d9edf7;
        padding: 10px;
        border-radius: 5px;
        margin-top: 8px;
        font-size: 0.9em;
      }
    `;
    
    // Append it to the document head
    document.head.appendChild(styleEl);
    
    // Clean up function to remove the style when component unmounts
    return () => {
      const existingStyle = document.getElementById('message-page-styles');
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  const getRecipientOptions = () => {
    if (!recipients || recipients.length === 0) return null;

    // Filter recipients based on the selected tab
    let filteredRecipients = [...recipients];

    if (recipientFilter === 'student') {
      filteredRecipients = recipients.filter(r => r.role === 'student');
    } else if (recipientFilter === 'parent') {
      filteredRecipients = recipients.filter(r => r.role === 'parent');
    }

    // For absent/late, we already have the right recipients from the API

    // Format the options
    if (recipientFilter === 'absent' || recipientFilter === 'late') {
      // For attendance filters, add a "Select All" option and then show parent names
      return (
        <>
          {filteredRecipients.length > 1 && (
            <option key="select-all" value="all">
              Select All ({filteredRecipients.length} parents)
            </option>
          )}
          {filteredRecipients.map(recipient => (
            <option key={recipient._id} value={recipient._id}>
              {recipient.name}
            </option>
          ))}
        </>
      );
    } else {
      // Group by role for other filters
      const groupedRecipients = filteredRecipients.reduce((acc, recipient) => {
        const type = recipient.role || 'unknown';
        if (!acc[type]) acc[type] = [];
        acc[type].push(recipient);
        return acc;
      }, {});

      return (
        <>
          {Object.entries(groupedRecipients).map(([type, list]) => (
            <optgroup key={type} label={`${type.charAt(0).toUpperCase() + type.slice(1)}s`}>
              {list.map(recipient => (
                <option key={recipient._id} value={recipient._id}>
                  {recipient.name}
                </option>
              ))}
            </optgroup>
          ))}
        </>
      );
    }
  };

  return (
    <div className="new-message-container">
      <div className="header">
        <h1>New Message</h1>
        <Link to="/messages" className="back-btn">
          Back to Messages
        </Link>
      </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading recipients...</p>
        </div>
      ) : error ? (
        <div className="error">
          <div className="error-icon">❌</div>
          <p>{error}</p>
          <button
            className="retry-button"
            onClick={fetchData}
          >
            Try Again
          </button>
        </div>
      ) : (
        <div className="message-form-container">
          <div className="recipient-filter-tabs">
            <button
              type="button"
              className={recipientFilter === 'student' ? 'active' : ''}
              onClick={() => handleRecipientFilterChange('student')}
            >
              Student
            </button>
            <button
              type="button"
              className={recipientFilter === 'parent' ? 'active' : ''}
              onClick={() => handleRecipientFilterChange('parent')}
            >
              Parent
            </button>
            <button
              type="button"
              className={recipientFilter === 'class' ? 'active' : ''}
              onClick={() => handleRecipientFilterChange('class')}
            >
              Class
            </button>
            {/* Only show Absent and Late tabs for admin and super_admin */}
            {user && (user.role === 'admin' || user.role === 'super_admin') && (
              <>
                <button
                  type="button"
                  className={recipientFilter === 'absent' ? 'active' : ''}
                  onClick={() => handleRecipientFilterChange('absent')}
                >
                  Absent
                </button>
                <button
                  type="button"
                  className={recipientFilter === 'late' ? 'active' : ''}
                  onClick={() => handleRecipientFilterChange('late')}
                >
                  Late
                </button>
              </>
            )}
          </div>
          <form onSubmit={handleSubmit}>
            
            {/* Show appropriate recipient selection based on active tab */}
            {recipientFilter === 'class' ? (
              <div className="form-group">
                <label htmlFor="halaqaId">Class</label>
                <select
                  id="halaqaId"
                  name="halaqaId"
                  value={formData.halaqaId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a class</option>
                  {classes.map(classItem => (
                    <option key={classItem._id} value={classItem._id}>
                      {classItem.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : (recipientFilter === 'absent' || recipientFilter === 'late') ? (
              <div className="form-group">
                <label htmlFor="recipientId">Parents of {recipientFilter === 'absent' ? 'Absent' : 'Late'} Students</label>
                <select
                  id="recipientId"
                  name="recipientId"
                  value={formData.recipientId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a parent</option>
                  {getRecipientOptions()}
                </select>
                {recipients.length === 0 && (
                  <div className="helper-text warning">
                    <span className="warning-icon">⚠️</span> No parents found with {recipientFilter === 'absent' ? 'absent' : 'late'} students today.
                  </div>
                )}
                <div className="info-text">
                  Note: Attendance records for today must be submitted before parents will appear in this list.
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="recipientId">{recipientFilter === 'student' ? 'Student' : 'Parent'}</label>
                <select
                  id="recipientId"
                  name="recipientId"
                  value={formData.recipientId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a {recipientFilter === 'student' ? 'student' : 'parent'}</option>
                  {getRecipientOptions()}
                </select>
              </div>
            )}
            
            {/* Subject field removed as requested */}
            
            {/* Message Purpose and Template sections removed as requested */}
            
            {/* First Message Tips removed to avoid duplication */}

            <div className="form-group">
              <label htmlFor="content">Message</label>
              <div className="content-header">
                <span>Write your message below</span>
                {/* Example template button removed */}
              </div>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                placeholder="Type your message here..."
                rows="8"
                required
              ></textarea>

              <div className="message-tips">
                <h4>Message Tips:</h4>
                <p>Use these placeholders to personalize your message:</p>
                <ul>
                  <li>{'{{parentName}}'} - Parent's name</li>
                  <li>{'{{studentName}}'} - Student's name</li>
                </ul>
              </div>
              
              {/* Preview section removed */}
            </div>

            <div className="form-group">
              <label htmlFor="attachment">Attachment (Optional)</label>
              <input
                type="file"
                id="attachment"
                name="attachment"
                onChange={handleChange}
              />
              <small className="helper-text">
                Max file size: 10MB
              </small>
            </div>

            <div className="form-group sms-toggle">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="sendSms"
                  checked={formData.sendSms}
                  onChange={handleChange}
                />
                Also send as Text Message (SMS)
              </label>
              <small className="helper-text">
                SMS will be sent if the recipient has a valid phone number
              </small>
            </div>
            
            <div className="form-actions">
              <Link to="/messages" className="cancel-btn">
                Cancel
              </Link>
              <button 
                type="submit" 
                className="send-btn"
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default NewMessagePage;